/*global REACT_APP_BOL_URL:true*/
import React, { useMemo } from "react";
import { FileTextOutlined, SnippetsOutlined } from "@ant-design/icons";
import { Descriptions, Tooltip, Tag, Row, Button, List } from "antd";
import { connect } from "dva";
import { Link } from "umi";
import {
  VehicleTypeMap,
  GatePassStatus,
  Drivability,
  DrivabilityMap,
  VehicleSizeMap,
  ShipmentStatus,
  VehicleSize,
} from "@/utils/enum";
import { copyToClipBoard } from "@/utils/utils";
import Highlight from "@/components/Highlight";
import SignedLink from "@/components/SignedLink";
import { renderQuoteStatus } from "@/components/VehiclesList/utils";

const { Item } = Descriptions;

const VehicleDescription = ({
  vehicle,
  configPerm,
  withTags,
  highlightVin,
  showBOLLink,
  quote,
  configLang,
  ...props
}) => {
  const shipment = useMemo(() => vehicle.shipment, [vehicle?.shipment]);

  const tagsList = useMemo(() => {
    const tags = [
      renderQuoteStatus(quote, shipment?.status || null)[0],
      <Tag
        key={0}
        color={vehicle?.drivabilityType === Drivability.Inoperational ? "error" : "success"}
        style={{ marginLeft: 5, fontWeight: "bold" }}
      >
        {DrivabilityMap[vehicle?.drivabilityType]}
      </Tag>,
    ];
    if (vehicle.comments) {
      tags.push(
        <Tooltip color="#fff" title={vehicle.comments}>
          <Tag style={{ cursor: "pointer" }}>
            <SnippetsOutlined /> Notes
          </Tag>
        </Tooltip>,
      );
    }

    return <Row style={{ marginBottom: "8px" }}>{tags}</Row>;
  }, [vehicle?.drivabilityType, quote, shipment?.status, vehicle?.comments]);

  const vehicleTitle = useMemo(() => {
    const isTransporter = !!configPerm["CoreService.AvailableLoads"];

    if (!isTransporter) {
      return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    }

    if (shipment?.status >= ShipmentStatus.Assigned) {
      return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    }

    return `${vehicle.isLateModel ? "Late Model" : vehicle.year} ${VehicleSizeMap[vehicle.size]} ${
      VehicleTypeMap[vehicle.displayVehicleType]
    }`;
  }, [configPerm, vehicle, shipment]);

  const vehicleDescription = useMemo(() => {
    const isTransporter = !!configPerm["CoreService.AvailableLoads"];

    if (!isTransporter) return null;

    if (shipment?.status >= ShipmentStatus.Assigned) {
      return null;
    }

    // Do not render anything in case we do not know the size
    if (vehicle.size === VehicleSize.UNKNOWN) return null;

    const obfuscatedSubText = `(Ex: ${
      configLang?.CoreService[
        `Enum:SizeVehicleType:${vehicle.size}:DisplayVehicleType:${vehicle?.displayVehicleType}:Tooltip`
      ]
    })`;

    // Do not render anything in case we do not have size info for this vehicle
    if (obfuscatedSubText.includes("Size information unavailable")) return null;

    return (
      <div>
        <span>{obfuscatedSubText}</span>
      </div>
    );
  }, [configPerm, vehicle, shipment]);

  const showLinkForTransporter = useMemo(
    () =>
      (configPerm["CoreService.AssignedLoads"] || configPerm["CoreService.AvailableLoads"]) &&
      shipment?.status >= ShipmentStatus.Assigned,
    [configPerm, shipment],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          gap: 8,
        }}
      >
        <Tooltip color="#fff" title="Copy to clipboard">
          <strong
            style={{ cursor: "pointer" }}
            onClick={() => {
              copyToClipBoard(vehicleTitle);
            }}
          >
            {vehicleTitle}
          </strong>
        </Tooltip>
        {withTags && tagsList}
      </div>

      <List.Item.Meta description={vehicleDescription} />
      <Descriptions column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }} {...props}>
        {vehicle?.vin ? (
          <Item label="Vin" key="vin">
            <Tooltip color="#fff" title="Copy to clipboard">
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  copyToClipBoard(vehicle.vin);
                }}
              >
                {highlightVin ? <Highlight text={vehicle.vin} phrase={vehicle.vin} /> : vehicle.vin}
              </span>
            </Tooltip>
          </Item>
        ) : null}
        <Item label="Type" key="type">
          {vehicle?.otherType ?? VehicleTypeMap[vehicle.vehicleType ?? vehicle.displayVehicleType] ?? "Other"}
        </Item>

        <Item label="Trim Package">{vehicle.trimPackage}</Item>
        <Item label="Curb Weight">{vehicle.curbWeight}</Item>
        <Item label="Dimensions">{vehicle.exteriorDimensions}</Item>
        <Item label="Gate Pass" key="gatePassType">
          {vehicle.gatePassType === GatePassStatus.Attached ? (
            <SignedLink staticUrl={vehicle.gatePassUrl}>
              <Button
                download
                type="link"
                style={{
                  margin: 0,
                  padding: 0,
                  maxHeight: "22px",
                }}
              >
                Attached
              </Button>
            </SignedLink>
          ) : null}
          {vehicle.gatePassType === GatePassStatus.Pending ? "Pending" : null}

          {vehicle.gatePassType === GatePassStatus.NotRequired ? "Not Required" : null}
        </Item>
        <Item label="Stock Number" key="stockNumber">
          {vehicle.stockNumber}
        </Item>
        <Item label="Color" key="color">
          {vehicle?.color}
        </Item>
        <Item label="Body sub type">{vehicle.bodySubType}</Item>
        {(configPerm["CoreService.AdminShipments.Vehicles"] ||
          configPerm["CoreService.Shipments.Vehicles"] ||
          showLinkForTransporter) && (
          <Item>
            <Link to={`/vehicles/view?id=${vehicle.id}`} target="_blank">
              <Button
                type="link"
                style={{
                  margin: 0,
                  padding: 0,
                  maxHeight: "22px",
                }}
              >
                View Details
              </Button>
            </Link>
          </Item>
        )}
        {showBOLLink && (
          <Item>
            <a target="_blank" rel="noopener noreferrer" href={`${REACT_APP_BOL_URL}/?vehicleId=${vehicle.id}`}>
              <Button
                type="link"
                style={{
                  margin: 0,
                  padding: 0,
                  position: "relative",
                  bottom: 5,
                }}
              >
                View BOL <FileTextOutlined style={{ paddingRight: 5 }} />
              </Button>
            </a>
          </Item>
        )}
      </Descriptions>
    </div>
  );
};

VehicleDescription.defaultProps = {
  withTags: true,
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
  configLang: configs.configLang,
}))(VehicleDescription);
